import React, { Component, ErrorInfo, ReactNode } from "react";
import { trackException } from "../services/appInsights";

interface Props {
  children: ReactNode;
  /** When provided, renders this instead of the full-page crash screen. Use this for section-level boundaries. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
  };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidMount() {
    // Log unhandled promise rejections without crashing the UI.
    // API errors are already handled in the interceptor; this catches
    // truly unhandled async failures (e.g. forgotten try/catch in useEffect).
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    // Log uncaught synchronous errors that originate outside React's tree
    // (e.g. third-party scripts). These won't trigger the boundary UI.
    window.addEventListener('error', this.handleWindowError);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.removeEventListener('error', this.handleWindowError);
  }

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    // Prevent the browser from printing an uncaught error to the console
    // twice and suppress the default "Unhandled rejection" red overlay in dev.
    event.preventDefault();
    trackException(event.reason, { source: 'unhandledrejection' });
  };

  handleWindowError = (event: ErrorEvent) => {
    trackException(event.error ?? event.message, {
      source: 'window.onerror',
      fileName: event.filename,
      lineNumber: event.lineno,
      columnNumber: event.colno,
    });
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the full component stack — useful for debugging in production.
    trackException(error, { source: 'ErrorBoundary', componentStack: errorInfo.componentStack });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return <>{this.props.fallback}</>;
      }

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '32px',
            backgroundColor: '#f5f5f5',
            fontFamily: 'Libre Franklin, sans-serif',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '48px 40px',
              maxWidth: '520px',
              width: '100%',
              boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
            }}
          >
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>⚠️</div>
            <h1
              style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#003883',
                marginBottom: '12px',
              }}
            >
              Something went wrong
            </h1>
            <p style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>
              An unexpected error occurred. Please try refreshing the page or go back to the home screen.
            </p>
            {this.state.error?.message && (
              <p
                style={{
                  fontSize: '11px',
                  color: '#999',
                  fontStyle: 'italic',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  marginBottom: '24px',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.error.message}
              </p>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: '10px 28px',
                  borderRadius: '30px',
                  border: '2px solid #EE7D11',
                  background: '#fff',
                  color: '#EE7D11',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                style={{
                  padding: '10px 28px',
                  borderRadius: '30px',
                  border: 'none',
                  background: '#EE7D11',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;